import 'flag-icons/css/flag-icons.min.css';
// Story 070b — register i18next + react-i18next singleton before any
// component that calls useTranslation. Side-effect import: just
// loading the module triggers `i18n.use(initReactI18next).init(...)`
// synchronously (resources are inlined from @swiss-magic/i18n).
import './lib/i18n';
import { applyQueryLocaleOverride } from './lib/i18n';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { TenantNotFoundPage } from './components/TenantNotFoundPage';
import { resolveTenant } from './lib/tenants';
import { InstallAppBanner } from './components/InstallAppBanner';
import { EnableNotificationsBanner } from './components/EnableNotificationsBanner';
import { PoweredByFooter } from './components/PoweredByFooter';
import { injectManifestLink, registerServiceWorker } from './lib/pwa/register';
import { AuthProvider } from './contexts/AuthContext';
import { NetworkProvider } from './contexts/NetworkContext';
import { NetworkBanner } from './components/NetworkBanner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicOnlyRoute } from './components/PublicOnlyRoute';
import { AdminRoute } from './components/AdminRoute';
import { DirectorRoute } from './components/DirectorRoute';
import { RegisterPage } from './pages/RegisterPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { TournamentFormPage } from './pages/TournamentFormPage';
import { TournamentPublicPage } from './pages/TournamentPublicPage';
import { PlayersPage } from './pages/PlayersPage';
import { RoundsPage } from './pages/RoundsPage';
import { TournamentStandingsPage } from './pages/tournament-hub/TournamentStandingsPage';
import { StandingsPage } from './pages/StandingsPage';
import { CrossTablePage } from './pages/CrossTablePage';
import { PairingsPublicPage } from './pages/PairingsPublicPage';
import { StartingListPage } from './pages/StartingListPage';
import { PlayerParticipationPage } from './pages/PlayerParticipationPage';
import { PrintingCenterPage } from './pages/PrintingCenterPage';
import { PreferredTablesPage } from './pages/tournament-hub/PreferredTablesPage';
import { RatingExportPage } from './pages/tournament-hub/RatingExportPage';
import { TournamentHubLayoutRoute } from './pages/tournament-hub/TournamentHubLayoutRoute';
import { TournamentHubOverview } from './pages/tournament-hub/TournamentHubOverview';
import { CoDirectorsPage } from './pages/tournament-hub/CoDirectorsPage';
import { FideExportPage } from './pages/tournament-hub/FideExportPage';
import { CoDirectorInviteAcceptPage } from './pages/CoDirectorInviteAcceptPage';
import { DirectorMessageCenterPage } from './pages/tournament-hub/DirectorMessageCenterPage';
import { DirectorMessageThreadPage } from './pages/tournament-hub/DirectorMessageThreadPage';
import { TvStandingsPage } from './pages/TvStandingsPage';
import { TvPairingsPage } from './pages/TvPairingsPage';
import { TvStartingListPage } from './pages/TvStartingListPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { AuthConfirmPage } from './pages/AuthConfirmPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ProfilePage } from './pages/ProfilePage';
import { PlayerDashboardPage } from './pages/PlayerDashboardPage';
import { MessageCenterPage } from './pages/MessageCenterPage';
import { MessageThreadPage } from './pages/MessageThreadPage';
import { MyMatchesPage } from './pages/MyMatchesPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';
import { DirectorProfilePage } from './pages/DirectorProfilePage';
import { AdminDirectorsPage } from './pages/AdminDirectorsPage';
import { AdminOrganizationProfilePage } from './pages/AdminOrganizationProfilePage';
import { AdminFideListPage } from './pages/AdminFideListPage';
import { FidePairingsCheckerPage } from './pages/FidePairingsCheckerPage';
import { FideTournamentGeneratorPage } from './pages/FideTournamentGeneratorPage';
import { DirectorInviteAcceptPage } from './pages/DirectorInviteAcceptPage';
import { HomePage } from './pages/HomePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TemplatesPage } from './pages/TemplatesPage';
import './index.css';

// Story 061 — short-circuit before mounting App when the hostname doesn't
// match any configured tenant. Keeps the broken-tenant case obvious and
// avoids a white screen / runtime errors from a misconfigured Supabase.
// Story 070b — honour ?lng=xx-XX before mount, so the first paint
// is already in the requested locale (no FOUC during e2e smoke).
applyQueryLocaleOverride();

const tenant = resolveTenant(
  typeof window !== 'undefined' ? window.location.hostname : '',
);

if (!tenant) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <TenantNotFoundPage />
    </StrictMode>,
  );
} else {
  // Story 065 — PWA bits activate only on the player surface. The director
  // surface stays plain HTML: no manifest <link>, no service worker, no
  // install affordance. Done before mount so the manifest link is present
  // on the very first paint.
  if (tenant.surface === 'player') {
    injectManifestLink();
    void registerServiceWorker();
  }
  createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Toaster position="top-center" richColors expand={false} />
      <Routes>
        {/* TV pages: no auth, no network banner — standalone full-screen */}
        <Route path="/t/:slug/tv/standings" element={<TvStandingsPage />} />
        <Route path="/t/:slug/tv/pairings/:roundNumber" element={<TvPairingsPage />} />
        <Route path="/t/:slug/tv/starting-list" element={<TvStartingListPage />} />

        {/* Privacy policy: public reference page, no chrome — linked from
            the Google OAuth consent screen (where verifiers fetch it). */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />

        {/* All other routes: wrapped in auth + network providers */}
        <Route path="*" element={
          <AuthProvider>
            <NetworkProvider>
              <InstallAppBanner />
              <EnableNotificationsBanner />
              <NetworkBanner />
              <Routes>
                <Route path="/" element={<PublicOnlyRoute><HomePage /></PublicOnlyRoute>} />
                <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
                <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
                <Route path="/reset-password" element={<ProtectedRoute><ResetPasswordPage /></ProtectedRoute>} />
                <Route path="/dashboard" element={<DirectorRoute><DashboardPage /></DirectorRoute>} />
                <Route path="/tournaments/new" element={<DirectorRoute><TournamentFormPage /></DirectorRoute>} />
                <Route path="/tournaments/:id" element={<DirectorRoute><TournamentHubLayoutRoute /></DirectorRoute>}>
                  <Route index element={<TournamentHubOverview />} />
                  <Route path="edit" element={<TournamentFormPage />} />
                  <Route path="players" element={<PlayersPage />} />
                  <Route path="rounds" element={<RoundsPage />} />
                  <Route path="standings" element={<TournamentStandingsPage />} />
                  <Route path="co-directors" element={<CoDirectorsPage />} />
                  <Route path="print" element={<PrintingCenterPage />} />
                  <Route path="preferred-tables" element={<PreferredTablesPage />} />
                  <Route path="rating-export" element={<RatingExportPage />} />
                  <Route path="fide-export" element={<FideExportPage />} />
                  <Route path="messages" element={<DirectorMessageCenterPage />} />
                  <Route path="messages/new" element={<DirectorMessageThreadPage />} />
                  <Route path="messages/:threadId" element={<DirectorMessageThreadPage />} />
                </Route>
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/director/profile" element={<DirectorRoute><DirectorProfilePage /></DirectorRoute>} />
                <Route path="/templates" element={<DirectorRoute><TemplatesPage /></DirectorRoute>} />
                <Route path="/admin/directors" element={<AdminRoute><AdminDirectorsPage /></AdminRoute>} />
                <Route path="/admin/organization" element={<AdminRoute><AdminOrganizationProfilePage /></AdminRoute>} />
                <Route path="/admin/fide-list" element={<AdminRoute><AdminFideListPage /></AdminRoute>} />
                <Route path="/player" element={<Navigate to="/player/dashboard" replace />} />
                <Route path="/player/dashboard" element={<ProtectedRoute><PlayerDashboardPage /></ProtectedRoute>} />
                <Route path="/player/messages" element={<ProtectedRoute><MessageCenterPage /></ProtectedRoute>} />
                <Route path="/player/messages/new" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
                <Route path="/player/messages/:threadId" element={<ProtectedRoute><MessageThreadPage /></ProtectedRoute>} />
                <Route path="/player/tournaments/:slug/matches" element={<ProtectedRoute><MyMatchesPage /></ProtectedRoute>} />
                <Route path="/fide/pairings-checker" element={<FidePairingsCheckerPage />} />
                <Route path="/fide/tournament-generator" element={<FideTournamentGeneratorPage />} />
                <Route path="/t/:slug" element={<TournamentPublicPage />} />
                <Route path="/t/:slug/standings" element={<StandingsPage />} />
                <Route path="/t/:slug/crosstable" element={<CrossTablePage />} />
                <Route path="/t/:slug/pairings/:roundNumber" element={<PairingsPublicPage />} />
                <Route path="/t/:slug/starting-list" element={<StartingListPage />} />
                <Route path="/t/:slug/player/:startingRank" element={<PlayerParticipationPage />} />
                <Route path="/invite/:token" element={<InviteAcceptPage />} />
                <Route path="/director-invite/:token" element={<DirectorInviteAcceptPage />} />
                <Route path="/co-director-invite/:token" element={<CoDirectorInviteAcceptPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/auth/confirm" element={<AuthConfirmPage />} />
              </Routes>
              <PoweredByFooter />
            </NetworkProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
  );
}
